import { useState } from "react";
import { ListGroupItem, Button, Popover, PopoverBody, Input } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TeacherModuleAnswersAndDocs from "../Teacher/TeacherModuleAnswersAndDocs";
import axios from "../../services/api/api";
import { QAPI_URL } from "../../constants/api.constants";
import { toast } from "react-toastify";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { FormattedMessage, useIntl } from "react-intl";
import { isGy25TestModule } from "../../Utilities/moduleHelper";

const FACT_MODULE_COLOR = "#1b7fa8";
const TEST_MODULE_COLOR = "#20a8d8";

const TeacherModule = (props) => {
  const intl = useIntl();
  const [isStudyTaskPopoverOpen, setIsStudyTaskPopoverOpen] = useState(false);
  const [questionCountInput, setQuestionCountInput] = useState("");
  const [questionCountError, setQuestionCountError] = useState("");
  const [maxQuestions, setMaxQuestions] = useState(null);
  const [loadingMaxQuestions, setLoadingMaxQuestions] = useState(false);
  const isPreview = props.module.isPreview;

  const defaultQuestionCount = props.studyTasksDefaultQuestionCount;

  const handleOpenStudyTasksPopover = () => {
    setQuestionCountInput("");
    setQuestionCountError("");
    setMaxQuestions(null);
    setLoadingMaxQuestions(true);
    setIsStudyTaskPopoverOpen(true);

    axios
      .get(`/Course/GetStudyTasksQuestionCount/${props.module.moduleId}`)
      .then((response) => {
        const max = response.data?.result;
        setMaxQuestions(max > 0 ? max : null);

        if (defaultQuestionCount > 0) {
          setQuestionCountInput(
            `${max > 0 ? Math.min(defaultQuestionCount, max) : defaultQuestionCount}`,
          );
        }
      })
      .catch((err) => {
        setMaxQuestions(null);
        toast.warn(
          err.response?.data ||
            intl.formatMessage({ id: "CouldNotFetchStudyTasks" }),
          TOAST_CONFIG,
        );
      })
      .finally(() => {
        setLoadingMaxQuestions(false);
      });
  };

  const handleOpenStudyTasks = () => {
    const count = Number(questionCountInput);

    if (!Number.isInteger(count) || count < 1) {
      setQuestionCountError(
        intl.formatMessage({ id: "StudyTasksQuestionMinError" }),
      );
      return;
    }

    if (maxQuestions > 0 && count > maxQuestions) {
      setQuestionCountError(
        intl.formatMessage(
          { id: "StudyTasksQuestionRangeError" },
          { max: maxQuestions },
        ),
      );
      return;
    }

    axios
      .get(
        `/Course/GetStudyTasks/${props.module.moduleId}?questionCount=${count}`,
      )
      .then((response) => {
        const result = response.data?.result;
        if (!result) return;

        const data = JSON.parse(result);
        const win = window.open(
          `${QAPI_URL}/RT/RT?mode=6&lng=sv`,
          "",
          `fullscreen=yes,width=${window.screen.width},height=${window.screen.height}`,
        );
        if (!win) {
          toast.warn(intl.formatMessage({ id: "PopupBlocked" }), TOAST_CONFIG);
          return;
        }

        setIsStudyTaskPopoverOpen(false);
        setTimeout(() => {
          win.postMessage({ fun: "init", param: { Data: data } }, QAPI_URL);
        }, 3000);
      })
      .catch((err) => {
        toast.warn(
          err.response?.data ||
            intl.formatMessage({ id: "CouldNotFetchStudyTasks" }),
          TOAST_CONFIG,
        );
      });
  };

  const isTestModuleRow = isGy25TestModule(props.module);

  // A row with no counterpart leaves that slot empty; it still renders,
  // invisible, so names line up across rows.
  const factSlotModule = isTestModuleRow ? null : props.module;
  const testSlotModule =
    props.coupledTestModule ?? (isTestModuleRow ? props.module : null);
  const showFactSlot = factSlotModule != null || props.courseHasNonTestModules;
  const showTestSlot = testSlotModule != null || props.courseHasTestModules;

  const singleAnswersTitleId = isTestModuleRow
    ? "AnswersTestModule"
    : props.module.isFactModule
      ? "AnswersFactModule"
      : "Answers";

  const renderPlayButton = (targetModule, color, title) => (
    <Button
      className={`m-0 p-0 border-0 me-2 flex-shrink-0 ${
        targetModule ? "" : "invisible"
      }`}
      size="sm"
      color="link"
      disabled={!targetModule || isPreview}
      title={title}
      aria-label={title}
      onClick={() =>
        props.runModule(
          props.courseId,
          targetModule.moduleId,
          targetModule.url,
          targetModule.moduleType,
          targetModule.isFinalExam && targetModule.isGy25,
        )
      }
    >
      <FontAwesomeIcon
        icon="circle-play"
        style={{ fontSize: 20, width: "1em" }}
        color={color}
      />
    </Button>
  );

  return (
    <ListGroupItem className={`px-2 ${isPreview ? "bg-light text-muted" : ""}`}>
      <div className="d-flex align-items-center w-100">
        {showFactSlot
          ? renderPlayButton(
              factSlotModule,
              FACT_MODULE_COLOR,
              intl.formatMessage({ id: "StartModule" }),
            )
          : null}
        {showTestSlot
          ? renderPlayButton(
              testSlotModule,
              TEST_MODULE_COLOR,
              intl.formatMessage({ id: "StartTest" }),
            )
          : null}

        <span
          className="text-truncate module-name-col ms-1"
          title={props.module.name}
        >
          {props.module.name}
        </span>

        <div className="ms-auto d-flex align-items-center ps-1 flex-shrink-0 gap-2">
          {isPreview ? (
            <span className="fst-italic small text-break">
              {props.module.previewText}
            </span>
          ) : (
            <>
              {props.studyTasksAvailable ? (
                <button
                  type="button"
                  id={`study-tasks-btn-${props.courseId}-${props.module.moduleId}`}
                  className="module-action-icon"
                  onClick={handleOpenStudyTasksPopover}
                  title={intl.formatMessage({ id: "StudyTasks" })}
                  aria-label={intl.formatMessage({ id: "StudyTasks" })}
                >
                  <img
                    src="img/Instuderingsfrågor.png"
                    alt=""
                    style={{ height: 20, width: "auto" }}
                  />
                </button>
              ) : null}
              {props.coupledTestModule ? (
                <>
                  <TeacherModuleAnswersAndDocs
                    module={props.module}
                    answersAvaliable={props.answersAvailable}
                    answersFileName={props.answersFileName}
                    titleId="AnswersFactModule"
                    iconColor={FACT_MODULE_COLOR}
                  />
                  <TeacherModuleAnswersAndDocs
                    module={props.coupledTestModule}
                    titleId="AnswersTestModule"
                    iconColor={TEST_MODULE_COLOR}
                  />
                </>
              ) : (
                <TeacherModuleAnswersAndDocs
                  module={props.module}
                  answersAvaliable={props.answersAvailable}
                  answersFileName={props.answersFileName}
                  titleId={singleAnswersTitleId}
                  iconColor={
                    isTestModuleRow ? TEST_MODULE_COLOR : FACT_MODULE_COLOR
                  }
                />
              )}

              {props.studyTasksAvailable ? (
                <Popover
                  target={`study-tasks-btn-${props.courseId}-${props.module.moduleId}`}
                  isOpen={isStudyTaskPopoverOpen}
                  toggle={() => setIsStudyTaskPopoverOpen(false)}
                  placement="bottom"
                  trigger="legacy"
                  popperClassName="study-tasks-popover"
                >
                  <PopoverBody className="p-3">
                    <div className="text-muted mb-3">
                      <strong>
                        <FormattedMessage
                          id="StudyTasksQuestionPrompt"
                          values={{ moduleName: props.module.name }}
                        />
                      </strong>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max={maxQuestions > 0 ? `${maxQuestions}` : undefined}
                        value={questionCountInput}
                        disabled={loadingMaxQuestions}
                        bsSize="sm"
                        className="text-center"
                        style={{ width: "48px" }}
                        onChange={(e) => {
                          setQuestionCountInput(e.target.value);
                          setQuestionCountError("");
                        }}
                      />
                      {maxQuestions > 0 ? (
                        <span className="text-muted text-nowrap">
                          / {maxQuestions}
                        </span>
                      ) : null}
                      <Button
                        size="sm"
                        color="primary"
                        className="text-nowrap ms-auto"
                        disabled={loadingMaxQuestions}
                        onClick={handleOpenStudyTasks}
                      >
                        <FormattedMessage id="FetchStudyTasks" />
                      </Button>
                    </div>
                    {questionCountError ? (
                      <div className="text-danger small mt-2">
                        {questionCountError}
                      </div>
                    ) : null}
                  </PopoverBody>
                </Popover>
              ) : null}
            </>
          )}
        </div>
      </div>
    </ListGroupItem>
  );
};

export default TeacherModule;
